import React, { useState } from 'react';
import { Typography, Input, Tooltip } from 'antd';
import { 
  SearchOutlined, 
  RightOutlined, 
  DownOutlined
} from '@ant-design/icons';
import { NODE_DEFINITIONS, NODE_CATEGORIES, getNodeIcon } from '../../utils/nodeTypes';

const { Text } = Typography;
const { Search } = Input;

const NodePalette = ({ onNodeDragStart, onNodeAdd }) => {
  const [searchText, setSearchText] = useState('');
  // All categories expanded by default
  const [expandedCategories, setExpandedCategories] = useState(Object.keys(NODE_CATEGORIES));

  const handleDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
    onNodeDragStart?.(nodeType);
  };

  const handleNodeClick = (nodeType) => {
    // Add node to center of canvas when clicked
    onNodeAdd?.(nodeType);
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => 
      prev.includes(categoryName) 
        ? prev.filter(cat => cat !== categoryName)
        : [...prev, categoryName]
    );
  };

  // Filter nodes based on search
  const filteredCategories = Object.keys(NODE_CATEGORIES).reduce((acc, categoryName) => {
    const category = NODE_CATEGORIES[categoryName];
    const filteredNodes = category.nodes.filter(nodeType => {
      const nodeDef = NODE_DEFINITIONS[nodeType];
      if (!nodeDef) return false;
      
      if (!searchText) return true;
      
      return (
        nodeDef.name.toLowerCase().includes(searchText.toLowerCase()) ||
        nodeDef.description.toLowerCase().includes(searchText.toLowerCase())
      );
    });

    if (filteredNodes.length > 0) {
      acc[categoryName] = { ...category, nodes: filteredNodes };
    }
    
    return acc;
  }, {});

  const renderCategoryHeader = (categoryName) => {
    const isExpanded = expandedCategories.includes(categoryName);
    
    return (
      <div
        onClick={() => toggleCategory(categoryName)}
        style={{
          padding: '8px 16px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          borderBottom: '1px solid #f5f5f5',
          backgroundColor: '#fafafa',
          transition: 'background-color 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f0f0f0';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#fafafa';
        }}
      >
        {isExpanded ? (
          <DownOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} />
        ) : (
          <RightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} />
        )}
        <Text style={{ 
          fontSize: '12px', 
          fontWeight: 600, 
          color: '#262626',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {categoryName}
        </Text>
      </div>
    );
  };

  const renderNodeItem = (nodeType) => {
    const nodeDef = NODE_DEFINITIONS[nodeType];
    if (!nodeDef) return null;
    
    return (
      <Tooltip 
        key={nodeType}
        title={nodeDef.description}
        placement="right"
        mouseEnterDelay={0.3}
      >
        <div
          draggable
          onDragStart={(e) => handleDragStart(e, nodeType)}
          onClick={() => handleNodeClick(nodeType)}
          style={{
            padding: '8px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.2s ease',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            border: '1px solid transparent',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f5ff';
            e.currentTarget.style.borderColor = '#d6e4ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.borderColor = 'transparent';
          }}
        >
          {/* Node Icon */}
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              border: '1px solid #d9d9d9',
              background: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              color: '#595959'
            }}
          >
            {getNodeIcon(nodeType)}
          </div>

          {/* Node Title */}
          <Text style={{ 
            fontSize: '11px', 
            fontWeight: 'bold', 
            color: '#595959',
            textAlign: 'center',
            lineHeight: 1.2,
            maxWidth: '100%',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {nodeDef.name}
          </Text>
        </div>
      </Tooltip>
    );
  };

  const renderNodesGrid = (nodes) => {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '8px',
        padding: '12px'
      }}>
        {nodes.map(nodeType => renderNodeItem(nodeType))}
      </div>
    );
  };

  return (
    <div 
      className="node-palette-container"
      style={{ 
        width: 320, 
        height: 'calc(100vh - 43px)',
        backgroundColor: '#ffffff',
        borderRight: '1px solid #f0f0f0',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Search Header */}
      <div style={{ 
        padding: '16px', 
        borderBottom: '1px solid #f0f0f0'
      }}>
        <Search
          placeholder="Search data components"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          bordered={false}
          style={{
            backgroundColor: '#fafafa',
            borderRadius: '6px'
          }}
        />
      </div>

      {/* Categories */}
      <div style={{ 
        flex: 1, 
        overflow: 'auto'
      }}>
        {/* All other categories */}
        {Object.keys(filteredCategories).map(categoryName => (
          <div key={categoryName}>
            {renderCategoryHeader(categoryName)}
            {expandedCategories.includes(categoryName) && (
              renderNodesGrid(filteredCategories[categoryName].nodes)
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NodePalette;